import React, { useCallback, useState } from 'react';
import HomeSearchPanel from './HomeSearchPanel';
import HomeFeedScopeToggle from './HomeFeedScopeToggle';

export default function HomeDiscoverySection({
  homeCrewCount,
  homeSearchTerm,
  setHomeSearchTerm,
  searchLoading,
  searchResults,
  onOpenTrial,
  homeFeedScope,
  setHomeFeedScope,
  followingCount,
  liveNowTrials,
  recentTrials,
  newTrialIds,
  TrialCard,
  featuredBankCards,
  FEATURED_TRIAL_TEMPLATES,
  createFeaturedTrialAndOpen,
  featuredBankLoading,
  locallyHiddenTrialIds,
  authUser,
  loadFeaturedBankCards,
  showLiveHero = true,
  showBrowseSections = true,
  isFirstSession = false,
  secondaryMode = false,
}) {
  const [starterOpeningKey, setStarterOpeningKey] = useState(null);
  const [starterOpenError, setStarterOpenError] = useState(null);
  const hasCrewScopedLiveTrials = !!authUser?.id && homeCrewCount > 0 && homeFeedScope === 'crews' && liveNowTrials.length > 0;
  const showGeneralLiveTrials = showLiveHero && liveNowTrials.length > 0 && !hasCrewScopedLiveTrials;
  const showFeaturedStarters = homeFeedScope !== 'following' && liveNowTrials.length === 0;

  const handleStarterOpen = useCallback(async (template) => {
    const openingKey = String(template?.id || template?.key || template?.title || 'featured');
    if (starterOpeningKey) return;
    setStarterOpeningKey(openingKey);
    setStarterOpenError(null);
    const result = await createFeaturedTrialAndOpen(template);
    if (!result?.ok && result?.reason !== 'auth_required') {
      setStarterOpenError('Could not open a starter trial right now. Please try again.');
    }
    setStarterOpeningKey(null);
  }, [createFeaturedTrialAndOpen, starterOpeningKey]);

  return (
    <div className={`space-y-5 ${secondaryMode ? 'opacity-90' : ''}`}>
      {showLiveHero && hasCrewScopedLiveTrials && (
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">From Your Crews</div>
          <div className="mb-1 text-lg font-extrabold text-slate-50">Your people need a vote.</div>
          <div className="mb-4 text-sm text-slate-400">Jump into the crew debate that is actually waiting on you.</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {liveNowTrials.map((trial) => (
              <TrialCard key={trial.id} trial={trial} isNew={newTrialIds.has(trial.id)} />
            ))}
          </div>
        </div>
      )}

      {showGeneralLiveTrials && (
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-300/80">Jump In</div>
          <div className="mb-1 text-lg font-extrabold text-slate-50">Live trials need a vote.</div>
          <div className="mb-4 text-sm text-slate-400">Pick one and react. No setup required.</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {liveNowTrials.map((trial) => (
              <TrialCard key={trial.id} trial={trial} isNew={newTrialIds.has(trial.id)} />
            ))}
          </div>
        </div>
      )}

      {showBrowseSections && (
        <div className="rounded-xl border border-slate-900 bg-slate-950/20 p-3 sm:p-4">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Browse or Search</div>
          <div className="space-y-3">
            <HomeFeedScopeToggle
              homeFeedScope={homeFeedScope}
              setHomeFeedScope={setHomeFeedScope}
              homeSearchTerm={homeSearchTerm}
              compact={false}
            />
            <HomeSearchPanel
              homeSearchTerm={homeSearchTerm}
              setHomeSearchTerm={setHomeSearchTerm}
              searchLoading={searchLoading}
              searchResults={searchResults}
              onOpenTrial={onOpenTrial}
            />
          </div>
        </div>
      )}

      {showBrowseSections && homeFeedScope === 'following' && liveNowTrials.length === 0 && followingCount === 0 && recentTrials.length === 0 ? (
        <div className="rounded-xl border border-slate-700 bg-slate-800/70 p-5 text-center">
          <div className="text-sm font-bold text-slate-100">You&apos;re not following anyone yet.</div>
          <div className="mt-2 text-sm text-slate-400">Follow people to see their trials here.</div>
          <div className="mt-2 text-xs text-slate-500">Look for the Follow button on trial cards.</div>
        </div>
      ) : showBrowseSections && showFeaturedStarters && (
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {secondaryMode ? 'More to Vote On' : (isFirstSession ? 'Start Here' : 'Keep Going')}
          </div>
          <div className={`mb-1 ${secondaryMode ? 'text-base font-bold text-slate-200' : 'text-lg font-extrabold text-slate-50'}`}>
            {secondaryMode
              ? (isFirstSession ? 'Start with one easy one.' : 'No live pile-on right now.')
              : (isFirstSession ? 'Cast your first vote.' : 'No live pile-on right now.')}
          </div>
          {!secondaryMode && (
            <>
              <div className="mb-1 text-sm text-slate-300">
                Start with one of these always-on prompts.
              </div>
              <div className="mb-4 text-sm text-slate-400">Pick one, cast your vote, then come back for the verdict.</div>
            </>
          )}
          <div className={`rounded-xl ${secondaryMode ? 'border border-slate-800 bg-slate-950/28' : 'border border-slate-700 bg-slate-800/70'} p-4 text-slate-300`}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {(featuredBankCards.length ? featuredBankCards : FEATURED_TRIAL_TEMPLATES).slice(0, 3).map((template, idx) => {
                const templateCardKey = String(template.id || template.key || template.title || idx);
                const isOpening = starterOpeningKey === templateCardKey;
                return (
                  <div key={templateCardKey} className={`rounded-lg border ${secondaryMode ? 'border-slate-800 bg-slate-950/45' : 'border-slate-700 bg-slate-900/60'} p-3`}>
                    <div className="text-sm font-bold text-slate-100">{template.emoji || '⚖️'} {template.title}</div>
                    <div className="mt-1 text-xs text-slate-400">
                      {(template.sideA || template.side_a)} vs {(template.sideB || template.side_b)}
                    </div>
                    <button
                      disabled={Boolean(starterOpeningKey)}
                      onClick={() => handleStarterOpen((featuredBankCards.length ? featuredBankCards[idx] : template))}
                      className={`mt-3 w-full rounded-lg py-2 text-xs font-bold disabled:cursor-not-allowed ${
                        secondaryMode
                          ? 'bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:bg-slate-800/70'
                          : 'bg-purple-600 text-white hover:bg-purple-500 disabled:bg-purple-700/70'
                      }`}
                    >
                      {isOpening ? 'Opening…' : 'Vote now'}
                    </button>
                  </div>
                );
              })}
            </div>
            {starterOpenError && (
              <div className="mt-3 rounded-lg border border-amber-700/50 bg-amber-950/40 px-3 py-2 text-xs text-amber-100">
                {starterOpenError}
              </div>
            )}
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 text-[11px] text-slate-500">
                {featuredBankLoading ? 'Refreshing featured prompts…' : `Fresh prompts rotate automatically.${locallyHiddenTrialIds.size ? ` (${locallyHiddenTrialIds.size} hidden locally)` : ''}`}
              </div>
              {!!authUser?.id && (
                <button
                  onClick={() => loadFeaturedBankCards('manual')}
                  className="self-start rounded-lg bg-slate-700 px-2.5 py-1 text-[11px] font-semibold text-slate-200 hover:bg-slate-600 sm:self-auto"
                >
                  Refresh picks
                </button>
              )}
            </div>
            {!authUser?.id && (
              <div className="mt-4 text-center text-xs text-slate-400">
                Sign in first to launch featured trials.
              </div>
            )}
          </div>
        </div>
      )}

      {showBrowseSections && recentTrials.length > 0 && (
        <div>
          <div className="mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">More to Browse</div>
            <div className="mt-1 text-sm font-semibold text-slate-300">Recent trials</div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {recentTrials.map((trial) => (
              <TrialCard key={`recent_${trial.id}`} trial={trial} isNew={newTrialIds.has(trial.id)} />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
